import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, FileText, CheckCircle2, Circle, Calendar, FileCheck2, ArrowRight } from 'lucide-react';
import { apiService } from '@/lib/api';
import LenderShell, { LENDER_BLUE, LENDER_NAVY } from '@/components/lender/LenderShell';
import { getLenderActor, money, fmtDate, fmtDateTime, APP_STATUS } from '@/lib/lenderDemo';

const EV = {
  submitted: 'Request submitted', docs_requested: 'Documents requested',
  docs_uploaded: 'Documents uploaded', approved: 'Loan approved', closed: 'Closed & funded',
};

export default function LenderApplicationReview() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const actor = getLenderActor();
  const [d, setD] = useState(null);
  const [mode, setMode] = useState(null);        // 'approve' | 'docs'
  const [closingDate, setClosingDate] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [pickDocs, setPickDocs] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!actor?.id) return;
    apiService.lenderApplicationDetail(appId, actor.id).then(({ data }) => {
      setD(data);
      const sum = (data.application.bid_lines || []).reduce((t, l) => t + (Number(l.amount) || 0), 0);
      setApprovedAmount(String(Math.round(sum) || data.application.requested_amount || ''));
    }).catch(() => setD(false));
  }, [actor?.id, appId]);
  useEffect(() => { load(); }, [load]);

  if (d === false) return <LenderShell active="applications"><p className="text-slate-400">Application not found or outside your access.</p></LenderShell>;
  if (!d) return <LenderShell active="applications"><p className="text-slate-400">Loading…</p></LenderShell>;

  const app = d.application;
  const chip = APP_STATUS[app.status] || APP_STATUS.in_progress;
  const lib = d.doc_library || [];
  const provided = app.documents || [];
  const isProvided = (k) => provided.some((x) => x.key === k && x.provided);

  const requestDocs = async () => {
    const required = lib.filter((x) => pickDocs[x.key]).map((x) => ({ key: x.key, label: x.label }));
    if (required.length === 0) return;
    setBusy(true);
    try { await apiService.lenderAppRequestDocs(actor.id, appId, { required_docs: required }); setMode(null); setPickDocs({}); load(); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try { await apiService.lenderAppApprove(actor.id, appId, { closing_date: closingDate || undefined, approved_amount: Number(approvedAmount) || undefined }); setMode(null); load(); }
    finally { setBusy(false); }
  };
  const close = async () => {
    setBusy(true);
    try { const { data } = await apiService.lenderAppClose(actor.id, appId, {}); load(); if (data.loan_id) navigate(`/lender/loans`); }
    finally { setBusy(false); }
  };

  return (
    <LenderShell active="applications">
      <button onClick={() => navigate('/lender/applications')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3">
        <ArrowLeft className="w-4 h-4" /> Back to applications
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>{app.borrower_name} · {app.project_name}</h1>
          <p className="text-slate-500 text-sm">{app.address || app.lot_location} {app.path === 'existing' && <span className="ml-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-600">existing DrawReady user</span>}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>{money(app.requested_amount)}</div>
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Bid sheet */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm flex items-center justify-between" style={{ color: LENDER_NAVY }}>
              <span>Submitted bid sheet</span><span className="text-xs font-normal text-slate-400">{(app.bid_lines || []).length} lines</span>
            </div>
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-sm">
                <tbody>
                  {(app.bid_lines || []).map((l, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="px-4 py-2 font-medium" style={{ color: LENDER_NAVY }}>{l.line}</td>
                      <td className="px-4 py-2 text-slate-400 text-xs">{l.category}</td>
                      <td className="px-4 py-2 text-right text-slate-700">{money(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="font-semibold text-sm mb-3 flex items-center gap-1.5" style={{ color: LENDER_NAVY }}><FileText className="w-4 h-4" /> Documents</div>
            <div className="space-y-1.5">
              {provided.filter((x) => x.provided).map((x) => (
                <div key={x.key} className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span className="text-slate-600">{x.label}</span>{x.filename && <span className="text-[11px] text-slate-400">{x.filename}</span>}</div>
              ))}
              {(app.outstanding_docs || []).map((lbl) => (
                <div key={lbl} className="flex items-center gap-2 text-sm"><Circle className="w-4 h-4 text-amber-400" /><span className="text-amber-700">{lbl}</span><span className="text-[11px] text-amber-600">requested</span></div>
              ))}
            </div>
          </div>

          {/* Actions */}
          {d.can_act && app.status !== 'closed' && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              {app.status === 'approved' ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-emerald-700 mb-3"><Calendar className="w-4 h-4" /> Approved for {money(app.approved_amount)} · closing {fmtDate(app.closing_date)}</div>
                  <button onClick={close} disabled={busy} data-testid="app-close"
                    className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white" style={{ background: LENDER_BLUE }}>
                    <FileCheck2 className="w-4 h-4" /> {busy ? 'Finalizing…' : 'Finalize at closing — set up budget & release funds'}
                  </button>
                  <p className="text-[11px] text-slate-400 mt-2">Maps each bid-sheet line to its construction phase and makes funds available to the borrower.</p>
                </>
              ) : mode === 'approve' ? (
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Approved budget (schedule total)</label>
                      <input type="number" value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)} data-testid="app-approved-amount" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
                    <div><label className="block text-xs font-medium text-slate-500 mb-1">Closing date</label>
                      <input type="date" value={closingDate} onChange={(e) => setClosingDate(e.target.value)} data-testid="app-closing-date" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={approve} disabled={busy} data-testid="app-confirm-approve" className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white" style={{ background: LENDER_BLUE }}><Check className="w-4 h-4" /> Approve loan</button>
                    <button onClick={() => setMode(null)} className="text-sm px-4 py-2 rounded-lg border border-slate-300 text-slate-600">Cancel</button>
                  </div>
                </div>
              ) : mode === 'docs' ? (
                <div className="space-y-3">
                  <div className="text-sm font-medium" style={{ color: LENDER_NAVY }}>Request documents from the borrower</div>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {lib.map((x) => (
                      <button key={x.key} onClick={() => setPickDocs((s) => ({ ...s, [x.key]: !s[x.key] }))} data-testid={`app-pickdoc-${x.key}`}
                        disabled={isProvided(x.key)} className="flex items-center gap-2 text-sm text-left disabled:opacity-40">
                        {pickDocs[x.key] ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-slate-300" />}
                        <span className="text-slate-600">{x.label}</span>{isProvided(x.key) && <span className="text-[10px] text-emerald-600">have</span>}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={requestDocs} disabled={busy} data-testid="app-confirm-request-docs" className="text-sm font-semibold px-4 py-2 rounded-lg text-white" style={{ background: LENDER_BLUE }}>Send request</button>
                    <button onClick={() => setMode(null)} className="text-sm px-4 py-2 rounded-lg border border-slate-300 text-slate-600">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setMode('docs')} data-testid="app-request-docs" className="text-sm font-semibold px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">Request documents</button>
                  <button onClick={() => setMode('approve')} data-testid="app-approve" className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white" style={{ background: LENDER_BLUE }}><Check className="w-4 h-4" /> Approve & set closing</button>
                </div>
              )}
            </div>
          )}
          {app.status === 'closed' && (
            <div className="bg-white border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800 flex items-center justify-between" data-testid="app-closed-note">
              <span>Closed & funded. The borrower can now draw against this loan.</span>
              {app.loan_id && <button onClick={() => navigate('/lender/loans')} className="flex items-center gap-1 font-semibold" style={{ color: LENDER_BLUE }}>View loan <ArrowRight className="w-4 h-4" /></button>}
            </div>
          )}
        </div>

        {/* Activity */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 h-fit">
          <div className="font-semibold text-sm mb-3" style={{ color: LENDER_NAVY }}>Activity</div>
          <ol className="space-y-3">
            {(app.events || []).map((e) => (
              <li key={e.id} className="flex gap-2.5" data-testid={`app-event-${e.type}`}>
                <Circle className="w-3 h-3 mt-1" style={{ color: LENDER_BLUE, fill: LENDER_BLUE }} />
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: LENDER_NAVY }}>{EV[e.type] || e.type}</div>
                  {e.note && <div className="text-xs text-slate-500">{e.note}</div>}
                  <div className="text-[11px] text-slate-400 mt-0.5">{e.actor_name} · {fmtDateTime(e.created_at)}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </LenderShell>
  );
}
